import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { defaultPortal, PORTALS } from "./lib/portals";
import Layout from "./components/Layout";
import { Spinner, Empty } from "./components/kit";
import { Login, Register, Forgot, AcceptInvite, AuthCallback } from "./pages/Auth";
import PortalDashboard from "./pages/Dashboard";
import MatchRoom from "./pages/referee/MatchRoom";
import MatchControls from "./pages/referee/Controls";
import RefereeDashboard, { TacticalEntry } from "./pages/referee/RefereeDashboard";
import CoachDashboard from "./pages/coach/CoachDashboard";
import LeagueDashboard from "./pages/league/LeagueDashboard";
import DemoWalkthrough from "./pages/DemoWalkthrough";
import PortalHome from "./pages/PortalHome";
import { ClubSubscription, PaymentResult } from "./pages/club/Subscription";
import { Appointments, LiveMatches, RefereeReports, Devices } from "./pages/referee/RefereePages";
import TacticsBoard from "./pages/coach/Tactics";
import { CoachTeams, CoachMatches, Training } from "./pages/coach/CoachPages";
import { ClubTeams, ClubPlayers, ClubStaff, ClubFixtures, ClubFinances } from "./pages/club/ClubPages";
import { Competitions, Fixtures, Standings, Disciplinary, Referees, Approvals, Affiliates } from "./pages/league/LeaguePages";
import { OfficialsWorkspace, CommissionerChecklists } from "./pages/officials/OfficialsPages";
import MatchView from "./pages/MatchView";
import { VideoReview, VideoHistory, Academy, Analytics, PlayerStats, FanMatches, FanCompetitions, Notifications, Profile } from "./pages/OtherPages";
import { AdminOverview, AdminUsers, AdminOrgs, AdminDevices, AdminAudit } from "./pages/admin/AdminPages";

function Guard({ portal, children }) {
  const { me } = useAuth();
  const loc = useLocation();
  if (me === null) return <Spinner />;
  if (!me) return <Navigate to={`/login?redirect=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (portal && !me.portals.includes(portal))
    return <Layout><Empty testid="not-authorised" text={`Your account is not authorised for the ${PORTALS[portal]?.label || portal} portal.`} /></Layout>;
  return <Layout>{children}</Layout>;
}

const Root = () => {
  const { me } = useAuth();
  if (me === null) return <Spinner />;
  return <Navigate to={me ? "/dashboard" : "/login"} replace />;
};

const DashRedirect = () => {
  const { me } = useAuth();
  if (me === null) return <Spinner />;
  if (!me) return <Navigate to="/login" replace />;
  const p = localStorage.getItem("tro_portal");
  const portal = me.portals.includes(p) ? p : defaultPortal(me.portals);
  return <Navigate to={PORTALS[portal].nav[0][1]} replace />;
};

function OAuthGate({ children }) {
  const loc = useLocation();
  if (loc.hash?.includes("session_id=")) return <AuthCallback />;
  return children;
}

const R = (path, portal, el) => <Route key={path} path={path} element={<Guard portal={portal}>{el}</Guard>} />;

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <OAuthGate>
          <Routes>
            <Route path="/" element={<Root />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<Forgot />} />
            <Route path="/accept-invitation" element={<AcceptInvite />} />
            <Route path="/dashboard" element={<DashRedirect />} />
            {R("/notifications", null, <Notifications />)}
            {R("/demo", null, <DemoWalkthrough />)}
            {R("/profile", null, <Profile />)}
            {R("/settings", null, <Profile settings />)}
            {R("/referee", "referee", <RefereeDashboard />)}
            {R("/referee/tactical", "referee", <TacticalEntry />)}
            {R("/referee/appointments", "referee", <Appointments />)}
            {R("/referee/matches", "referee", <LiveMatches />)}
            {R("/referee/matches/:id/tactical", "referee", <MatchRoom />)}
            {R("/referee/matches/:id/controls", "referee", <MatchControls />)}
            {R("/referee/reports", "referee", <RefereeReports />)}
            {R("/referee/devices", "referee", <Devices />)}
            {R("/coach", "coach", <CoachDashboard />)}
            {R("/coach/teams", "coach", <CoachTeams />)}
            {R("/coach/tactics", "coach", <TacticsBoard />)}
            {R("/coach/matches", "coach", <CoachMatches />)}
            {R("/coach/training", "coach", <Training />)}
            {R("/club", "club", <PortalDashboard portal="club" />)}
            {R("/club/teams", "club", <ClubTeams />)}
            {R("/club/players", "club", <ClubPlayers />)}
            {R("/club/subscription", "club", <ClubSubscription />)}
            {R("/payment/result", null, <PaymentResult />)}
            {R("/club/staff", "club", <ClubStaff />)}
            {R("/club/fixtures", "club", <ClubFixtures />)}
            {R("/club/training", "club", <Training club />)}
            {R("/club/finances", "club", <ClubFinances />)}
            {R("/league", "league", <LeagueDashboard />)}
            {R("/league/competitions", "league", <Competitions />)}
            {R("/league/fixtures", "league", <Fixtures />)}
            {R("/league/appointments", "league", <Fixtures appointments />)}
            {R("/league/standings", "league", <Standings />)}
            {R("/league/disciplinary", null, <Disciplinary />)}
            {R("/league/referees", "league", <Referees />)}
            {R("/league/approvals", "league", <Approvals />)}
            {R("/federation", "federation", <PortalHome portal="federation" />)}
            {R("/federation/affiliates", "federation", <Affiliates />)}
            {R("/federation/referees", "federation", <Referees />)}
            {R("/officials", "officials", <PortalDashboard portal="officials" />)}
            {R("/officials/appointments", "officials", <Appointments />)}
            {R("/officials/matches", "officials", <OfficialsWorkspace />)}
            {R("/commissioner", "commissioner", <PortalDashboard portal="commissioner" />)}
            {R("/commissioner/checklists", "commissioner", <CommissionerChecklists />)}
            {R("/commissioner/reports", "commissioner", <OfficialsWorkspace commissioner />)}
            {R("/video", "video", <PortalHome portal="video" />)}
            {R("/video/matches", "video", <VideoReview />)}
            {R("/video/reviews", "video", <VideoHistory />)}
            {R("/academy", "academy", <PortalHome portal="academy" />)}
            {R("/academy/courses", "academy", <Academy />)}
            {R("/academy/assessments", "academy", <Academy assessments />)}
            {R("/analytics", "analytics", <Analytics />)}
            {R("/analytics/matches", "analytics", <Analytics section="matches" />)}
            {R("/analytics/referees", "analytics", <Analytics section="referees" />)}
            {R("/player", "player", <PortalHome portal="player" />)}
            {R("/player/fixtures", "player", <CoachMatches player />)}
            {R("/player/statistics", "player", <PlayerStats />)}
            {R("/fan", "fan", <PortalHome portal="fan" />)}
            {R("/fan/matches", "fan", <FanMatches />)}
            {R("/fan/competitions", "fan", <FanCompetitions />)}
            {R("/admin", "admin", <AdminOverview />)}
            {R("/admin/users", "admin", <AdminUsers />)}
            {R("/admin/organisations", "admin", <AdminOrgs />)}
            {R("/admin/devices", "admin", <AdminDevices />)}
            {R("/admin/audit-logs", "admin", <AdminAudit />)}
            {R("/matches/:id", null, <MatchView />)}
            {R("/matches/:id/live", null, <MatchView tab="live" />)}
            {R("/matches/:id/events", null, <MatchView tab="events" />)}
            {R("/matches/:id/reports", null, <MatchView tab="reports" />)}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </OAuthGate>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
